import {
  OpenLockerActionHandlers,
  OpenLockerActionProps,
  OpenLockerActionStates,
  OpenResultFailure,
  OpenResultSuccess,
  useOpenLockerAction
} from './open-locker-action.hook';
import { DialogActions, DialogContent } from '@mui/material';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { Alert, AlertTitle } from '@mui/material';
import { Dropdown } from '../../../../../ui/components/pure/dropdown/dropdown.component';
import { getUpdateStatusOptions } from '../../../../orders/details/actions/open-locker/open-locker-action.constants';
import { ValidOrderStatusChangeOption } from '../../../../orders/details/actions/open-locker/open-locker-action.hook';
import { Snackbar } from '../../../../../ui/components/pure/snackbar/snackbar.component';
import { OpenLockerRequestView } from './shared-components/open-locker-request-view.component';
import { DeliveryContainerContentsModel } from '../../../models/delivery-container-contents.model';
import { OpenLockerNotPermitted } from './shared-components/open-locker-not-permitted.component';
import { useBookingFlowType } from '../../../../orders/list/actions/book-order/use-booking-flow-type.hook';
import { BookingFlowType } from '../../../../../ui/enums/booking-flow-type.enum';

export function OpenLockerActionModal({ lockerID, projectID, onAbort, ...rest }: OpenLockerActionProps) {
  const { state, handlers, hub, lockerContents, multipleDoors, canOpenLocker, isError, isLoading } =
    useOpenLockerAction({
      lockerID,
      projectID,
      onAbort,
      ...rest
    });
  if (isError) {
    return (
      <Snackbar severity="error" onClose={() => onAbort()}>
        Sorry there was an error getting the details of the locker.{' '}
      </Snackbar>
    );
  }

  if (canOpenLocker?.canOpen === false) {
    return <OpenLockerNotPermitted onClose={onAbort} />;
  }

  return (
    <OpenLockerModalComponent
      state={state}
      handlers={handlers}
      loading={isLoading}
      hubName={hub?.name}
      deliveryContainerContents={lockerContents}
      multipleDoors={multipleDoors}
      projectID={projectID}
    />
  );
}

export function OpenLockerModalComponent({
  hubName,
  handlers,
  state,
  deliveryContainerContents,
  loading,
  multipleDoors,
  projectID
}: {
  hubName?: string;
  state: OpenLockerActionStates;
  handlers: OpenLockerActionHandlers;
  loading: boolean;
  deliveryContainerContents?: DeliveryContainerContentsModel[];
  multipleDoors?: {
    doorOptions: { label: string; value: string }[];
    onSelectDoor(doorID: string): void;
  };
  projectID: string;
}) {
  const bookingFlowType = useBookingFlowType(projectID);
  const showStatusChange =
    deliveryContainerContents?.some((c) => c.delivery?.isInFinalState === false) &&
    bookingFlowType !== BookingFlowType.INVENTORY;

  if (state.current === 'OPEN_REQUEST') {
    return (
      <Modal
        title={'Open locker?'}
        loading={loading || state.loading}
        open={true}
        maxWidth="xs"
        onClose={handlers.onCancelIntent}
      >
        <OpenLockerRequestView
          error={state.error}
          hubName={hubName}
          multipleDoors={multipleDoors}
          onCancelIntent={handlers.onCancelIntent}
          onSendRequest={handlers.onSendRequest}
        />
      </Modal>
    );
  }
  if (state.current === 'OPEN_REQUEST_SUCCESS') {
    return (
      <Modal
        title={showStatusChange ? 'Also change the status?' : 'Success'}
        loading={state.loading}
        open={true}
        maxWidth="xs"
        onClose={handlers.onCancelIntent}
      >
        <OpenLockerSuccessView showStatusChange={showStatusChange} projectID={projectID} {...handlers} {...state} />
      </Modal>
    );
  }
  if (state.current === 'OPEN_REQUEST_FAILURE') {
    return (
      <Modal open={true} title={'Open locker?'} maxWidth="xs" onClose={handlers.onCancelIntent}>
        <LockerOpenedFailedView {...handlers} {...state} />
      </Modal>
    );
  } else {
    return null;
  }
}

export function LockerOpenedFailedView({
  openLockerResponse,
  error,
  onSendRequest,
  onContinueToStatusChange
}: OpenResultFailure & OpenLockerActionHandlers) {
  let errorTitle = 'Something went wrong.';
  let errorMessage = 'There was an unexpected error.';
  let prompt = 'It looks like the door did not open. Please retry.';
  let showContinueButton = false;

  if (openLockerResponse) {
    if (openLockerResponse.tryCount > 1) {
      if (openLockerResponse.tryCount > openLockerResponse.failCount) {
        prompt = 'It looks like some doors did not open. Please retry.';
      } else {
        prompt = 'It looks like no doors opened. Please retry.';
      }
    }

    const jammedCount = openLockerResponse.failReasons.filter((reason) => reason === 'jammed').length;

    if (jammedCount > 0) {
      errorTitle = 'Some doors are jammed.';
      errorMessage = `${jammedCount} door${jammedCount > 1 ? 's are' : ' is'} jammed.`;
    } else {
      if (openLockerResponse.failCount > 0) {
        errorTitle = 'Failed to open some doors.';
        errorMessage = `${openLockerResponse.failCount} out of ${openLockerResponse.tryCount} door${
          openLockerResponse.tryCount > 1 ? 's' : ''
        } failed to open.`;
      }
    }

    if (openLockerResponse.tryCount > openLockerResponse.failCount) {
      prompt = 'If you were able to do what you needed, please click continue.';
      showContinueButton = true;
    }
  }

  return (
    <>
      <DialogContent>
        {error ? (
          <Alert variant="outlined" severity="error">
            <AlertTitle>Something went wrong</AlertTitle>
            There was an error opening the locker
          </Alert>
        ) : null}
        <p>
          {openLockerResponse?.status === 'success' ? (
            <Alert variant="outlined" severity="success">
              <p style={{ margin: 0, height: '100%', display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                Locker opened
              </p>
            </Alert>
          ) : (
            <>
              <p>
                <Alert variant="outlined" severity="error">
                  <AlertTitle>{errorTitle}</AlertTitle>
                  {errorMessage}
                </Alert>
              </p>
              <p>{prompt}</p>
            </>
          )}
        </p>
      </DialogContent>
      <DialogActions>
        {openLockerResponse?.status === 'failed' || error ? (
          <TextButton color="primary" variant="outlined" onClick={onSendRequest}>
            Retry
          </TextButton>
        ) : null}
        {showContinueButton ? (
          <TextButton color="secondary" variant="outlined" onClick={onContinueToStatusChange}>
            Continue
          </TextButton>
        ) : null}
      </DialogActions>
    </>
  );
}

export function OpenLockerSuccessView(
  props: {
    newStatus?: ValidOrderStatusChangeOption;
    showStatusChange?: boolean;
    projectID: string;
  } & OpenResultSuccess &
    OpenLockerActionHandlers
) {
  const bookingFlowType = useBookingFlowType(props.projectID);
  const callToActionButtonText = props.showStatusChange ? 'Change' : 'Done';
  const callToActionButtonHandler = props.showStatusChange ? props.onStatusChange : props.onDone;
  const statusOptions = getUpdateStatusOptions(bookingFlowType);
  return (
    <>
      <DialogContent>
        <p>
          <Alert variant="outlined" severity="success">
            <p style={{ margin: 0, height: '100%', display: 'flex', alignItems: 'center', fontSize: '14px' }}>
              Locker opened
            </p>
          </Alert>
        </p>
        {props.error && (
          <p>
            <Alert variant="outlined" severity="error">
              <AlertTitle>Something went wrong</AlertTitle>
              There was an error updating the status of the order
            </Alert>
          </p>
        )}
        {props.showStatusChange ? (
          <>
            <p>Do you need to update the parcel status?</p>
            <Dropdown
              fullWidth
              shrinkLabel={false}
              dropdownVariant="primarySolid"
              textFieldDesktopWidth="60%"
              menuItems={statusOptions}
              placeholder="Select new status"
              value={props.newStatus}
              onChange={(e) => props.onStatusInput(e.target.value as ValidOrderStatusChangeOption)}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        {props.showStatusChange && (
          <TextButton color="secondary" variant="outlined" onClick={() => props.onDone()}>
            Skip
          </TextButton>
        )}
        <TextButton color="primary" variant="outlined" onClick={() => callToActionButtonHandler()}>
          {callToActionButtonText}
        </TextButton>
      </DialogActions>
    </>
  );
}
